import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { REGISTER_MUTATION } from '../../apollo/queries/userQueries';
import '../css/CustomerRegisterPage.css';

const FormRow = ({ label, required, className, children }) => (
  <div className={`form-row ${className || ''}`}>
    <div className="form-label">
      {label}
      {required && <span className="required">*</span>}
    </div>
    <div className="form-input-container">{children}</div>
  </div>
);

const CustomerRegisterPage = () => {
  const navigate = useNavigate();
  
  // 폼 데이터 상태
  const [formData, setFormData] = useState({
    // 회사 정보
    ceoName: '',
    company: '',
    zipCode: '',
    address: '',
    addressDetail: '',
    businessNumber: '',
    businessType: '',
    businessSector: '',
    
    // 로그인 정보
    email: '',
    loginId: '',
    password: '',
    passwordConfirm: '',
    
    // 사용자 정보
    name: '',
    department: '',
    phone: '',
    mobile: '',
    fax: '',
    workType: ''
  });

  // 폼 상태
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);

  // 사용자 등록 뮤테이션
  const [registerUser, { loading }] = useMutation(REGISTER_MUTATION, {
    onCompleted: (data) => {
      alert('고객이 성공적으로 등록되었습니다.');
      navigate('/admin/customer');
    },
    onError: (error) => {
      console.error('고객 등록 에러:', error);
      alert(`고객 등록 실패: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  // Daum 주소검색 API 초기화
  useEffect(() => {
    // 이미 로드된 경우 재로드하지 않음
    if (window.daum && window.daum.Postcode) {
      return;
    }

    const script = document.createElement('script');
    script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // 컴포넌트 언마운트 시 정리
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // 입력값 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 메시지 클리어
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 등록한 이메일 사용 버튼 클릭
  const handleUseEmail = () => {
    if (formData.email) {
      setFormData(prev => ({
        ...prev,
        loginId: prev.email
      }));
    } else {
      alert('먼저 이메일을 입력해주세요.');
    }
  };

  // 주소 검색 열기
  const handleAddressSearch = () => {
    if (!window.daum || !window.daum.Postcode) {
      alert('주소검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setIsAddressSearchOpen(true);
    
    new window.daum.Postcode({
      oncomplete: function(data) {
        setFormData(prev => ({
          ...prev,
          zipCode: data.zonecode,
          address: data.address
        }));
        setIsAddressSearchOpen(false);
      },
      onclose: function() {
        setIsAddressSearchOpen(false);
      },
      theme: {
        bgColor: "#FFFFFF",
        searchBgColor: "#0B65C8",
        contentBgColor: "#FFFFFF",
        pageBgColor: "#FFFFFF",
        textColor: "#333333",
        queryTextColor: "#FFFFFF",
        postcodeTextColor: "#FA4256",
        emphTextColor: "#008BD3",
        outlineColor: "#E0E0E0"
      }
    }).open();
  };

  // 폼 유효성 검사
  const validateForm = () => {
    const newErrors = {};

    // 필수 필드 검사
    if (!formData.ceoName.trim()) newErrors.ceoName = '대표자명을 입력해주세요.';
    if (!formData.company.trim()) newErrors.company = '회사명을 입력해주세요.';
    if (!formData.zipCode.trim()) newErrors.zipCode = '우편번호를 입력해주세요.';
    if (!formData.address.trim()) newErrors.address = '기본 주소를 입력해주세요.';
    if (!formData.addressDetail.trim()) newErrors.addressDetail = '상세 주소를 입력해주세요.';
    if (!formData.businessNumber.trim()) newErrors.businessNumber = '사업자등록번호를 입력해주세요.';
    if (!formData.businessType.trim()) newErrors.businessType = '업태를 입력해주세요.';
    if (!formData.businessSector.trim()) newErrors.businessSector = '업종을 입력해주세요.';
    
    if (!formData.email.trim()) newErrors.email = '이메일을 입력해주세요.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = '올바른 이메일 형식이 아닙니다.';
    
    if (!formData.loginId.trim()) newErrors.loginId = '로그인 ID를 입력해주세요.';
    if (!formData.password.trim()) newErrors.password = '비밀번호를 입력해주세요.';
    else if (formData.password.length < 4 || formData.password.length > 15) newErrors.password = '비밀번호는 4~15자로 입력해주세요.';
    else if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(formData.password)) newErrors.password = '영문과 숫자를 모두 포함해야 합니다.';
    
    if (!formData.passwordConfirm.trim()) newErrors.passwordConfirm = '비밀번호 확인을 입력해주세요.';
    else if (formData.password !== formData.passwordConfirm) newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
    
    if (!formData.name.trim()) newErrors.name = '담당자명을 입력해주세요.';
    if (!formData.phone.trim()) newErrors.phone = '전화번호를 입력해주세요.';
    if (!formData.mobile.trim()) newErrors.mobile = '휴대전화를 입력해주세요.';
    if (!formData.workType.trim()) newErrors.workType = '담당 업무를 선택해주세요.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 폼 제출
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await registerUser({
        variables: {
          input: {
            email: formData.email,
            password: formData.password,
            name: formData.name,
            phone: formData.phone,
            mobile: formData.mobile,
            company: formData.company,
            ceoName: formData.ceoName,
            zipCode: formData.zipCode,
            address: formData.address,
            addressDetail: formData.addressDetail,
            businessNumber: formData.businessNumber,
            department: formData.department,
            fax: formData.fax
          }
        }
      });
    } catch (error) {
      // 에러는 onError에서 처리됨
      console.error('고객 등록 실행 에러:', error);
    }
  };

  return (
    <>
      <div className="breadcrumb">
        <span>홈 &gt; 회원 정보 관리 &gt; 회원 등록하기</span>
      </div>
      <div className="admin-main-content">
        <aside className="admin-sidebar">
          <div className="sidebar-menu">
            <h3>회원 정보 관리</h3>
            <ul>
              <li onClick={() => navigate('/admin/customer')}>회원 정보 관리</li>
              <li className="active">회원 등록하기</li>
              <li onClick={() => navigate('/admin/customer/blocked')}>차단 회원 관리</li>
            </ul>
          </div>
          <div className="sidebar-menu">
            <h3>가입통계</h3>
            <ul>
              <li onClick={() => navigate('/admin/stats/signup')}>월별 가입 통계</li>
            </ul>
          </div>
        </aside>
        
        <main className="admin-content">
          <div className="customer-register-container">
            <div className="page-header">
              <div className="page-title-bar"></div>
              <h2>회원 등록하기</h2>
            </div>

            <div className="form-section">
              <div className="section-title-container">
                <h3>회사 정보</h3>
              </div>
              <div className="form-table">
                <FormRow label="대표자명" required>
                  <input 
                    type="text" 
                    name="ceoName"
                    value={formData.ceoName}
                    onChange={handleInputChange}
                    placeholder="대표자명을 입력해주세요"
                  />
                  {errors.ceoName && <div className="error-message">{errors.ceoName}</div>}
                </FormRow>
                <FormRow label="회사명" required>
                  <input 
                    type="text" 
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="예 : 부스바월드" 
                  />
                  {errors.company && <div className="error-message">{errors.company}</div>}
                </FormRow>
                <FormRow label="우편번호" required>
                  <input 
                    type="text" 
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    placeholder="우편 번호" 
                    className="short-input"
                    readOnly 
                  />
                  <button 
                    type="button"
                    className="btn-sm btn-dark" 
                    onClick={handleAddressSearch}
                    disabled={isAddressSearchOpen}
                  >
                    {isAddressSearchOpen ? '검색 중...' : '주소 검색'}
                  </button>
                  {errors.zipCode && <div className="error-message">{errors.zipCode}</div>}
                </FormRow>
                <FormRow label="기본 주소" required>
                  <input 
                    type="text" 
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="주소 검색을 클릭해주세요." 
                    readOnly
                  />
                  {errors.address && <div className="error-message">{errors.address}</div>}
                </FormRow>
                <FormRow label="상세 주소" required>
                  <input 
                    type="text" 
                    name="addressDetail"
                    value={formData.addressDetail}
                    onChange={handleInputChange}
                    placeholder="상세 주소를 입력해 주세요." 
                  />
                  {errors.addressDetail && <div className="error-message">{errors.addressDetail}</div>}
                </FormRow>
                <FormRow label="사업자 등록 번호" required className="multi-field-row">
                  <input 
                    type="text" 
                    name="businessNumber"
                    value={formData.businessNumber}
                    onChange={handleInputChange}
                    placeholder="예 : 123-45-67890" 
                  />
                  {errors.businessNumber && <div className="error-message">{errors.businessNumber}</div>}
                  <div className="form-sub-field">
                    <div className="form-label">
                      업태<span className="required">*</span>
                    </div>
                    <input 
                      type="text" 
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleInputChange}
                      placeholder="예 : 제조업" 
                    />
                    {errors.businessType && <div className="error-message">{errors.businessType}</div>}
                  </div>
                  <div className="form-sub-field">
                    <div className="form-label">
                      업종<span className="required">*</span>
                    </div>
                    <input 
                      type="text" 
                      name="businessSector"
                      value={formData.businessSector}
                      onChange={handleInputChange}
                      placeholder="예 : 전자부품" 
                    />
                    {errors.businessSector && <div className="error-message">{errors.businessSector}</div>}
                  </div>
                </FormRow>
              </div>
            </div>

            <div className="form-section">
              <div className="section-title-container">
                <h3>로그인 정보</h3>
              </div>
              <div className="form-table">
                <FormRow label="이메일 주소" required>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="이메일 주소를 입력해주세요"
                  />
                  {errors.email && <div className="error-message">{errors.email}</div>}
                </FormRow>
                <FormRow label="로그인 ID" required>
                  <input 
                    type="text" 
                    name="loginId"
                    value={formData.loginId}
                    onChange={handleInputChange}
                    placeholder="예 : busbar@busbarworld.com" 
                    className="long-input" 
                  />
                  <button 
                    type="button"
                    className="btn-sm btn-dark"
                    onClick={handleUseEmail}
                  >
                    등록한 이메일 사용
                  </button>
                  <p className="input-hint full-width">한 번 등록한 ID는 변경할 수 없으니 주의해 주세요.</p>
                  {errors.loginId && <div className="error-message">{errors.loginId}</div>}
                </FormRow>
                <FormRow label="비밀번호" required>
                  <input 
                    type="password" 
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="비밀번호를 입력해 주세요."  
                    className="long-input"
                  />
                  <p className="input-hint long-hint">
                    4~15자로 입력해 주세요.<br/>
                    영문과 숫자를 모두 포함해야 합니다.<br/>
                    ID와 동일한 문자열은 사용할 수 없습니다.
                  </p>
                  {errors.password && <div className="error-message">{errors.password}</div>}
                </FormRow>
                <FormRow label="비밀번호 재확인" required>
                  <input 
                    type="password" 
                    name="passwordConfirm"
                    value={formData.passwordConfirm}
                    onChange={handleInputChange}
                    placeholder="확인을 위해 다시 입력해 주세요."  
                    className="long-input"
                  />
                  {errors.passwordConfirm && <div className="error-message">{errors.passwordConfirm}</div>}
                </FormRow>
              </div>
            </div>

            <div className="form-section">
              <div className="section-title-container">
                <h3>사용자 정보</h3>
              </div>
              <div className="form-table">
                <FormRow label="담당자" required>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="예 : 홍길동" 
                  />
                  {errors.name && <div className="error-message">{errors.name}</div>}
                </FormRow>
                <FormRow label="부서 / 역할">
                  <input 
                    type="text" 
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    placeholder="예 : 구매 담당자" 
                  />
                </FormRow>
                <FormRow label="전화번호" required className="multi-field-row">
                  <input 
                    type="text" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="예 : 02-1234-5478" 
                  />
                  {errors.phone && <div className="error-message">{errors.phone}</div>}
                  <div className="form-sub-field">
                    <div className="form-label">
                      휴대전화<span className="required">*</span>
                    </div>
                    <input 
                      type="text" 
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      placeholder="예 : 010-6543-1875" 
                    />
                    {errors.mobile && <div className="error-message">{errors.mobile}</div>}
                  </div>
                </FormRow>
                <FormRow label="FAX">
                  <input 
                    type="text" 
                    name="fax"
                    value={formData.fax}
                    onChange={handleInputChange}
                    placeholder="예 : 02-1234-5679"
                  />
                </FormRow>
                <FormRow label="담당 업무" required>
                  <select 
                    name="workType"
                    value={formData.workType}
                    onChange={handleInputChange}
                  >
                    <option value="">- 선택 -</option>
                    <option value="구매">구매</option>
                    <option value="영업">영업</option>
                    <option value="기술">기술</option>
                    <option value="관리">관리</option>
                    <option value="기타">기타</option>
                  </select>
                  {errors.workType && <div className="error-message">{errors.workType}</div>}
                </FormRow>
              </div>
            </div>

            <div className="page-actions">
                <button className="btn-secondary" onClick={() => navigate(-1)}>
                  <span className="arrow-icon">&lt;</span> 돌아가기
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || loading}
                >
                  {isSubmitting || loading ? '등록 중...' : '저장'} <span className="arrow-icon">&gt;</span>
                </button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CustomerRegisterPage; 